import { useRef, useState } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";
import { fmtInt } from "@/lib/utils";

export function ItemRow({ item, onChange, onRemove, idx, below = null }) {
  const { lang } = useI18n();
  // Snapshot the per-100g reference ONCE on mount so subsequent
  // quantity edits scale from the ORIGINAL density, never from the
  // just-scaled values (which would compound on every keystroke).
  //  · Prefer the item's own `per_100g` if the AI/barcode/label
  //    provided one.
  //  · Otherwise derive it from the item's current values / current
  //    quantity — that way manual entries + AI photo/describe items
  //    (which usually ship without `per_100g`) still auto-scale their
  //    macros when the user changes the quantity in the edit form.
  const p100Ref = useRef(null);
  if (p100Ref.current === null) {
    const stored = item.per_100g;
    const hasStored = stored && (stored.calories || stored.protein_g || stored.carbs_g || stored.fat_g);
    if (hasStored) {
      p100Ref.current = { ...stored };
    } else if (item.quantity_g && Number(item.quantity_g) > 0) {
      const q = Number(item.quantity_g);
      p100Ref.current = {
        calories:  (Number(item.calories)  || 0) * 100 / q,
        protein_g: (Number(item.protein_g) || 0) * 100 / q,
        carbs_g:   (Number(item.carbs_g)   || 0) * 100 / q,
        fat_g:     (Number(item.fat_g)     || 0) * 100 / q,
      };
    } else {
      p100Ref.current = {};
    }
  }
  const p100 = p100Ref.current;
  const hasP100 = p100 && (p100.calories || p100.protein_g || p100.carbs_g || p100.fat_g);
  const unit = item.unit || "g";
  const [estimating, setEstimating] = useState(false);
  const estimate = async () => {
    setEstimating(true);
    try {
      const { data } = await api.post("/meals/estimate-ingredient", { text: item.name, lang });
      p100Ref.current = { ...data.per_100g };
      onChange({
        quantity_g: data.quantity_g, unit: data.unit, per_100g: data.per_100g, assumption: data.assumption || null,
        calories: Math.round(data.calories), protein_g: data.protein_g, carbs_g: data.carbs_g, fat_g: data.fat_g,
      });
    } catch {
      toast.error(lang === "en" ? "Couldn't estimate — try again" : "تعذّر التقدير — حاول مرة ثانية");
    } finally {
      setEstimating(false);
    }
  };
  const setUnit = (u) => onChange({ unit: u });
  const applyQuantity = (grams) => {
    const g = Math.max(0, Number(grams) || 0);
    if (!hasP100) { onChange({ quantity_g: g }); return; }
    const f = g / 100;
    onChange({
      quantity_g: g,
      calories:  Math.round((p100.calories || 0) * f),
      protein_g: Math.round((p100.protein_g || 0) * f * 10) / 10,
      carbs_g:   Math.round((p100.carbs_g   || 0) * f * 10) / 10,
      fat_g:     Math.round((p100.fat_g     || 0) * f * 10) / 10,
    });
  };
  return (
    <div className="bg-neutral-50 rounded-2xl p-3" data-testid={`item-row-${idx}`}>
      <div className="flex items-center gap-2 mb-2">
        <input
          value={item.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={lang === "en" ? "Ingredient name" : "اسم المكون"}
          className="flex-1 min-w-0 bg-white border border-neutral-100 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
          data-testid={`item-name-${idx}`}
        />
        {(item.name || "").trim().length > 1 && (
          <button
            type="button" onClick={estimate} disabled={estimating}
            className="flex-shrink-0 h-8 px-2.5 rounded-full bg-brand-50 text-brand-600 text-[11px] font-bold flex items-center gap-1 disabled:opacity-60 transition-colors hover:bg-brand-100"
            data-testid={`estimate-item-${idx}`}
          >
            {estimating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {lang === "en" ? "Estimate" : "قدّر"}
          </button>
        )}
        <button onClick={onRemove} className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center" data-testid={`remove-item-${idx}`}>
          <X className="w-4 h-4" />
        </button>
      </div>
      {below}
      {item.assumption && (
        <div className="text-[11px] text-neutral-500 leading-relaxed text-end mb-2 px-1" data-testid={`item-assumption-${idx}`}>
          <span className="font-semibold text-neutral-600">{lang === "en" ? "Assumptions: " : "افتراضات: "}</span>{item.assumption}
        </div>
      )}
      {/* Quantity + unit toggle — always shown; when per-100g reference exists, edits auto-recalc macros */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mb-2" dir="rtl">
        <label className="text-[11px] text-neutral-500 flex-shrink-0">{lang === "en" ? "Amount:" : "الكمية:"}</label>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="1"
          value={item.quantity_g || ""}
          placeholder={lang === "en" ? "100" : "١٠٠"}
          onChange={(e) => applyQuantity(e.target.value)}
          className="w-16 flex-shrink-0 bg-white border border-neutral-100 rounded-lg px-2 py-1.5 text-xs num text-center focus:outline-none focus:border-brand-500 placeholder:text-neutral-300"
          data-testid={`item-quantity-${idx}`}
        />
        {/* Compact unit toggle: g (solids) vs ml (liquids). ml is treated 1:1 with g for macro scaling. */}
        <div className="flex flex-shrink-0 bg-white border border-neutral-100 rounded-lg overflow-hidden" data-testid={`item-unit-${idx}`}>
          <button
            type="button"
            onClick={() => setUnit("g")}
            className={`px-2.5 py-1 text-[11px] font-semibold ${unit === "g" ? "bg-brand-500 text-white" : "text-neutral-500"}`}
          >{lang === "en" ? "g" : "غ"}</button>
          <button
            type="button"
            onClick={() => setUnit("ml")}
            className={`px-2.5 py-1 text-[11px] font-semibold ${unit === "ml" ? "bg-brand-500 text-white" : "text-neutral-500"}`}
          >{lang === "en" ? "ml" : "مل"}</button>
        </div>
        {hasP100 && (
          <span className="text-[10px] text-brand-600 bg-brand-50 rounded-full px-2 py-0.5 mr-auto whitespace-nowrap">
            {lang === "en" ? "Values update automatically" : "القيم تتحدث تلقائياً"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <NumInput label={lang === "en" ? "kcal" : "سعرات"} value={item.calories} onChange={(v) => onChange({ calories: v })} />
        <NumInput label={lang === "en" ? "protein (g)" : "بروتين (غ)"} value={item.protein_g} onChange={(v) => onChange({ protein_g: v })} />
        <NumInput label={lang === "en" ? "carbs (g)" : "كارب (غ)"} value={item.carbs_g} onChange={(v) => onChange({ carbs_g: v })} />
        <NumInput label={lang === "en" ? "fat (g)" : "دهون (غ)"} value={item.fat_g} onChange={(v) => onChange({ fat_g: v })} />
      </div>
    </div>
  );
}

export function NumInput({ label, value, onChange, placeholder = "0" }) {
  // Empty-string display when value is 0/null/undefined so the input reads
  // as blank with a grey placeholder — users can type any number without
  // having to first select-and-delete the "0". onChange still emits a
  // Number (or "" when the field is cleared) so downstream math paths that
  // do `Number(x) || 0` continue to see 0 for empty inputs.
  const display = (value === 0 || value === null || value === undefined || value === "") ? "" : value;
  return (
    <label className="block">
      <div className="text-[10px] text-neutral-500 mb-0.5 text-center whitespace-nowrap truncate">{label}</div>
      <input
        type="number"
        inputMode="decimal"
        value={display}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-full bg-white border border-neutral-100 rounded-lg px-1 py-1.5 text-xs text-center num focus:outline-none focus:border-brand-500 placeholder:text-neutral-300"
      />
    </label>
  );
}

export function TotalCol({ label, v, suffix = "" }) {
  return (
    <div>
      <div className="text-xs text-brand-700">{label}</div>
      <div className="text-lg font-bold num text-brand-900 flex items-baseline justify-center gap-0.5" dir="rtl">
        <bdi>{fmtInt(v)}</bdi>{suffix && <span>{suffix}</span>}
      </div>
    </div>
  );
}
